import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption, IPersonaProps } from '@fluentui/react';
import {
  Checkbox,
  Field,
  FluentProvider,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Textarea,
  Tooltip,
  webLightTheme,
} from '@fluentui/react-components';
import React, { useEffect } from 'react';
import { Controller, FieldValues, UseFormMethods, useWatch } from 'react-hook-form';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import { IEntityTerminationActionItem } from '../types';
import ErrorHandlingPeoplePicker from '../../common/formFields/ErrorHandlingPeoplePicker';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';
import LegoCategoryDropdown from '../../common/formFields/LegoCategoryDropdown';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import { GET_TERMINATION_LIFECYCLE_STATUSES_QUERY } from '../../../utils/statApi/EntityStatusApi';
import {
  getEarliestTaskCompletedDate,
  getLatestTaskCompletedDate,
  getMinDateCompleted,
  onlyWhenChanged,
  parseCalendarDate,
  validateDateCompleted,
  validateNotAfterMaxDate,
} from '../utils/taskCompletionDates';

interface IBlockerEditFormProps {
  form: UseFormMethods<FieldValues>;
  selectedBlocker?: IEntityTerminationActionItem;
  existingBlockers?: IEntityTerminationActionItem[];
}

const EntityTerminationBlockerEditForm = (props: IBlockerEditFormProps): JSX.Element => {
  const { form, selectedBlocker, existingBlockers } = props;
  const { control, errors, setValue } = form;

  const today = moment().endOf('day').toDate();

  const completed = useWatch({
    control,
    name: 'completed',
    defaultValue: !!selectedBlocker?.dateCompleted,
  });

  const dateCompleted = useWatch({
    control,
    name: 'dateCompleted',
    defaultValue: selectedBlocker?.dateCompleted ?? null,
  });

  const dateStarted = useWatch({
    control,
    name: 'dateStarted',
    defaultValue: selectedBlocker?.dateStarted ?? null,
  });

  const isPrimaryBlocker = useWatch({
    control,
    name: 'isPrimaryBlocker',
    defaultValue: selectedBlocker?.isPrimaryBlocker ?? false,
  });

  const existingPrimaryBlocker = existingBlockers?.find(
    (b) => b.isPrimaryBlocker && b.id !== selectedBlocker?.id,
  );

  const hasOtherBlockers = existingBlockers?.some((b) => b.id !== selectedBlocker?.id) ?? false;

  const hasOtherOpenBlockers =
    existingBlockers?.some((b) => b.id !== selectedBlocker?.id && !b.dateCompleted) ?? false;

  const isNewBlocker = !selectedBlocker;

  const isExistingPrimaryBlocker = !!selectedBlocker?.isPrimaryBlocker;

  const isPrimaryBlockerLocked =
    !hasOtherBlockers || isExistingPrimaryBlocker || (isNewBlocker && !hasOtherOpenBlockers);

  const primaryBlockerLockedTooltipMessage = (): string => {
    if (!hasOtherBlockers) {
      return 'This is the only blocker, so it is automatically the primary blocker.';
    }
    if (isNewBlocker && !hasOtherOpenBlockers) {
      return 'All other blockers are complete, so this is automatically the primary blocker.';
    }
    return 'An entity must always have a primary blocker. To change the primary blocker, edit another blocker and mark it as primary.';
  };

  const showPrimaryBlockerWarning = !!isPrimaryBlocker && !!existingPrimaryBlocker;

  const showCompletingPrimaryBlockerWarning =
    !!isPrimaryBlocker && !!completed && hasOtherOpenBlockers;

  const minCompletedDate = parseCalendarDate(dateStarted);

  const dateCompletedDate = parseCalendarDate(dateCompleted);

  const maxStartedDate = dateCompletedDate && dateCompletedDate < today ? dateCompletedDate : today;

  // The blocker cannot be completed before the latest task Completed Date.
  const latestTaskCompletedDate = getLatestTaskCompletedDate(selectedBlocker?.tasks);

  // The blocker's Date Started cannot be after the earliest child task's
  // Completed Date.
  const earliestTaskCompletedDate = getEarliestTaskCompletedDate(selectedBlocker?.tasks);
  const maxDateStarted =
    earliestTaskCompletedDate && earliestTaskCompletedDate < maxStartedDate
      ? earliestTaskCompletedDate
      : maxStartedDate;
  const validateStartNotAfterEarliestTaskCompleted = onlyWhenChanged(
    selectedBlocker?.dateStarted,
    validateNotAfterMaxDate(
      earliestTaskCompletedDate,
      'earliest task Completed Date',
      'Date Started',
    ),
  );

  const minDateCompleted = getMinDateCompleted(minCompletedDate, latestTaskCompletedDate);

  useEffect(() => {
    if (dateCompleted && !completed) {
      setValue('completed', true, { shouldValidate: true, shouldDirty: false });
    }
  }, [dateCompleted, completed, setValue]);

  const isTaskCompleted = (task?: { completedDate?: string | null }): boolean => {
    if (!task?.completedDate) {
      return false;
    }
    const parsed = new Date(task.completedDate);
    return !Number.isNaN(parsed.getTime()) && parsed.getFullYear() > 1;
  };

  const hasIncompleteTasks = (selectedBlocker?.tasks ?? []).some((task) => !isTaskCompleted(task));

  const { data: lifecycleData } = useQuery(GET_TERMINATION_LIFECYCLE_STATUSES_QUERY);
  const blockerStatus = lifecycleData?.terminationLifecycleStatuses?.find(
    (s: IDropdownReference) => s.fieldName === 'Blocker',
  );

  const onLegoCategoryChange = (value: IDropdownOption): void => {
    setValue('legoCategory', value);
  };

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          name="legoCategory"
          label="Blocker Category"
          valueKey="legoCategory"
          control={control}
          defaultValue={selectedBlocker?.legoCategory ?? null}
          rules={{ required: 'Please select a blocker category.' }}
          render={({ value }) => {
            return (
              <LegoCategoryDropdown
                label="Blocker Category"
                valueKey="legoCategory"
                errors={errors}
                handleChange={onLegoCategoryChange}
                value={value}
                watchTerminationLifecycleStatus={blockerStatus}
                hideNonSelectable
                required
              />
            );
          }}
        />
        <Controller
          control={control}
          name="description"
          defaultValue={selectedBlocker?.description ?? ''}
          rules={{
            required: 'Please enter blocker description.',
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label="Blocker Description"
              required
              validationMessage={errors?.description?.message}
            >
              <Input
                id="description"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, data) => onChange(data.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="isPrimaryBlocker"
          defaultValue={isPrimaryBlockerLocked ? true : selectedBlocker?.isPrimaryBlocker ?? false}
          render={({ onChange, value }) => {
            if (isPrimaryBlockerLocked && value !== true) {
              setValue('isPrimaryBlocker', true, { shouldValidate: true, shouldDirty: false });
            }

            const checkbox = (
              <Checkbox
                id="isPrimaryBlocker"
                label="Primary Blocker?"
                checked={isPrimaryBlockerLocked || !!value}
                disabled={isPrimaryBlockerLocked}
                onChange={(_, data) => onChange(!!data.checked)}
              />
            );

            return isPrimaryBlockerLocked ? (
              <Tooltip
                content={primaryBlockerLockedTooltipMessage()}
                relationship="description"
                positioning="above-start"
              >
                <span style={{ display: 'inline-block' }}>{checkbox}</span>
              </Tooltip>
            ) : (
              checkbox
            );
          }}
        />
        {showPrimaryBlockerWarning && (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              There is already a primary blocker for this company. Saving this as the primary
              blocker will replace the existing one.
            </MessageBarBody>
          </MessageBar>
        )}
        <Controller
          name="owner"
          control={control}
          defaultValue={selectedBlocker?.owner ?? null}
          rules={{ required: 'Please add owner.' }}
          render={({ onChange, value }) => (
            <ErrorHandlingPeoplePicker
              controlName="owner"
              labelText="Owner"
              required
              itemLimit={1}
              selectedItems={value ? [value] : []}
              errorMessage={errors?.owner?.message}
              removeButtonAriaLabel="Remove Owner"
              onResolveSuggestions={onResolveSuggestions}
              onChange={(items: IPersonaProps[]) =>
                onChange((items?.[0] as IServiceContact) ?? null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="status"
          defaultValue={selectedBlocker?.status ?? ''}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Status">
              <Textarea
                id="status"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, data) => onChange(data.value)}
              />
            </Field>
          )}
        />
        <Controller
          control={control}
          name="dateStarted"
          defaultValue={selectedBlocker?.dateStarted ?? null}
          rules={{
            validate: {
              requiredWhenCompleted: (value) =>
                completed ? !!value || 'Date required when a blocker is completed.' : true,
              notAfterEarliestTaskCompleted: validateStartNotAfterEarliestTaskCompleted,
            },
          }}
          render={({ onChange, value }) => (
            <UtcDatePicker
              label="Date Started"
              allowTextInput
              disableAutoFocus={false}
              isRequired={!!completed}
              value={value}
              textField={{ name: 'dateStarted', errorMessage: errors?.dateStarted?.message }}
              onSelectDate={(utcDateString: string) => onChange(utcDateString)}
              maxDate={maxDateStarted}
            />
          )}
        />
        <Controller
          name="completed"
          control={control}
          defaultValue={!!selectedBlocker?.dateCompleted}
          rules={{
            validate: (value) => {
              if (!value) return true;
              if (hasIncompleteTasks) {
                return 'All blocker tasks must have a Completed Date before marking this blocker as completed.';
              }
              return true;
            },
          }}
          render={({ onChange, value }) => (
            <Field label="Completed?" required validationMessage={errors?.completed?.message}>
              <RadioGroup
                id="completed"
                value={value ? 'true' : 'false'}
                onChange={(_, data) => {
                  const isCompleted = data.value === 'true';
                  onChange(isCompleted);

                  if (!isCompleted) {
                    setValue('dateCompleted', null);
                  }
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
          )}
        />
        {showCompletingPrimaryBlockerWarning && (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              This is the primary blocker and there are other open blockers for this entity. After
              completing this blocker, you will need to designate another open blocker as the
              primary blocker.
            </MessageBarBody>
          </MessageBar>
        )}
        <Controller
          control={control}
          name="dateCompleted"
          defaultValue={selectedBlocker?.dateCompleted ?? null}
          rules={{
            validate: (value) =>
              validateDateCompleted(value, !!completed, latestTaskCompletedDate, 'a blocker'),
          }}
          render={({ onChange, value }) => (
            <UtcDatePicker
              label="Date Completed"
              allowTextInput
              disableAutoFocus={false}
              disabled={!completed}
              isRequired={!!completed}
              value={value}
              textField={{ name: 'dateCompleted', errorMessage: errors?.dateCompleted?.message }}
              onSelectDate={(utcDateString: string) => onChange(utcDateString)}
              maxDate={today}
              minDate={minDateCompleted}
            />
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default EntityTerminationBlockerEditForm;
